import debounce from "lodash.debounce";
import { parse } from "qs";

import CardHeader from "@/components/Includes/CardHeader/CardHeader.vue";
import DefaultTools from "@/components/Includes/DefaultTools/DefaultTools.vue";
import Loader from "@/components/Includes/Loader/Loader.vue";
import Pagination from "@/components/Includes/Pagination/Pagination.vue";
import TableEmpty from "@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue";
import IStatisticsReport from "@/interfaces/IStatisticsReport";
import ITableCol from "@/interfaces/ITableCol";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import getTableColumns from "@/libs/getTableColumns";
import { format, parseISO, startOfYesterday } from "date-fns";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

const statisticsReportModule = namespace("statisticsReport");
const actions = ["edit", "view", "delete"];

@Component({
  components: {
    Pagination,
    CardHeader,
    DefaultTools,
    TableEmpty,
    Loader,
  },
})
export default class StatisticsReports extends Vue {
  @statisticsReportModule.Action
  private readonly fetchStatisticsReports!: (params: {
    query: string;
  }) => Promise<{ links: number; data: IStatisticsReport[] }>;

  @statisticsReportModule.Action
  private readonly fetchStatisticsReport!: (params: {
    id: number;
  }) => Promise<IStatisticsReport>;

  @statisticsReportModule.Action
  private readonly updateStatisticsReport!: (params: {
    id: number;
    isDeleted: boolean;
  }) => Promise<IStatisticsReport>;

  private page = 1;
  private limit = "100";
  private totalItems = 0;
  private reports: IStatisticsReport[] = [];
  private isLoading = false;

  private selectedTableColumns: IMultiSelectItem[] = [];

  private get tableColumns(): ITableCol[] {
    const columns = getTableColumns(this.reports, false);

    return Array.isArray(columns)
      ? columns.concat(
          actions.map((action) => ({ key: action, sortable: false }))
        )
      : [];
  }

  private get columns() {
    return this.tableColumns.filter((column) => !actions.includes(column.key));
  }

  private get fields() {
    return this.tableColumns.filter(
      (column) =>
        actions.includes(column.key) ||
        this.selectedTableColumns.some((s) => s.value === column.key)
    );
  }

  private formatDate(date: string) {
    return format(parseISO(date), "dd.MM.yyyy HH:mm:ss");
  }

  private async filterData(idVal: string) {
    this.isLoading = true;
    const id = +idVal;

    if (id > 0) {
      try {
        const report = await this.fetchStatisticsReport({ id });

        this.reports = [report];
      } catch {
        this.reports = [];
      }
    } else if (id === 0) {
      await this.getReports();
    }

    this.isLoading = false;
  }

  private editReport(id: number) {
    this.goReport(id, "edit");
  }

  private viewStats(search: string) {
    const query = parse(search);

    query.date_start = format(startOfYesterday(), "yyyy-MM-dd");
    query.date_end = query.date_start;

    // @ts-ignore
    this.$router.push({ name: "Statistics", query });
  }

  private async deleteReport(id: number) {
    try {
      this.isLoading = true;
      await this.updateStatisticsReport({ isDeleted: true, id });
      this.getReports();
    } catch (error) {
      this.isLoading = false;
    }
  }

  private goReport(id: number, mode?: string) {
    this.$router.push({
      name: "statistics_report",
      params: {
        id: `${id}`,
      },
      query: {
        mode,
      },
    });
  }

  @Watch("page", { immediate: true })
  @Watch("limit")
  private async getReports() {
    this.isLoading = true;
    try {
      const { data, links } = await this.fetchStatisticsReports({
        query: `?pagination=true&page=${this.page}&limit=${this.limit}`,
      });

      this.totalItems = links;
      this.reports = data;
    } catch {
      this.reports = [];
      this.totalItems = 0;
      this.page = 1;
    }

    this.isLoading = false;
  }

  private created() {
    // @ts-ignore
    this.filterData = debounce(this.filterData.bind(this), 300);
  }
}
